import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  ApiError,
  STICKER_UPLOAD_CONTENT_TYPES,
  STICKER_UPLOAD_MAX_BYTES,
  placedStickerSchema,
  stickerConfirmBodySchema,
  stickerLibraryRowSchema,
  stickerLibrarySchema,
  stickerPlaceBodySchema,
  stickerPresignBodySchema,
  stickerPresignSchema,
  stickerRemoveBodySchema,
  stickerUpdateBodySchema,
  type CardDetail,
  type PlacedSticker,
  type StickerConfirmBody,
  type StickerLibraryRow,
  type StickerPlaceBody,
  type StickerPresign,
  type StickerPresignBody,
  type StickerUpdateBody,
} from '@molt/shared';
import { emptyResponseSchema, get, patch, post, request } from './api';
import { announce } from './announce';
import { queryKeys } from './query-keys';

/**
 * FE-80.1 -- the translator every string-authoring sticker export takes.
 *
 * Structurally identical to `InterfaceLanguageContextValue['t']` and to
 * `AttachmentTranslate` / `CardDateTranslate` / `ChecklistTranslate` /
 * `CommentTranslate` (every lib leaf declares its own). It is a **required
 * first parameter** rather than a `useInterfaceLanguage()` call inside each
 * hook on purpose: the context resolves against an English fallback catalogue
 * when no provider is mounted, so a hook that read it itself would render
 * English with nothing red. Taking it as an argument makes `tsc` enumerate
 * every call site. Required, never optional -- optionality is exactly what
 * stops the enumeration.
 *
 * The `count?` third parameter is carried even though this module declares no
 * plural: dropping it would make the type structurally incompatible with
 * `InterfaceLanguageContextValue['t']` at every call site.
 */
export type StickerTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export interface PlaceStickerVariables {
  readonly stickerId: string;
  readonly stickerName: string;
  readonly surface: StickerPlaceBody['surface'];
  readonly positionX: number;
  readonly positionY: number;
  readonly rotation?: number | undefined;
  readonly zIndex?: number | undefined;
}

export interface MoveStickerVariables {
  readonly placementId: string;
  readonly stickerName: string;
  readonly body: StickerUpdateBody;
}

export interface RemoveStickerVariables {
  readonly placementId: string;
  readonly stickerName: string;
}

export interface UploadCustomStickerVariables {
  readonly file: File;
  readonly name?: string | null | undefined;
}

export const STICKER_UPLOAD_ACCEPT = STICKER_UPLOAD_CONTENT_TYPES.join(',');

export function isStickerUploadContentType(
  value: string,
): value is StickerPresignBody['contentType'] {
  return STICKER_UPLOAD_CONTENT_TYPES.includes(
    value as StickerPresignBody['contentType'],
  );
}

export function getBoardStickers(boardId: string): Promise<StickerLibraryRow[]> {
  return get(`/boards/${encodeURIComponent(boardId)}/stickers`, stickerLibrarySchema);
}

export function presignCustomSticker(
  boardId: string,
  body: StickerPresignBody,
): Promise<StickerPresign> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/stickers/presign`,
    stickerPresignSchema,
    stickerPresignBodySchema.parse(body),
  );
}

/**
 * `t` is required and first, mirroring `putAttachmentBytes`: this is not a React
 * component, so it must never call `useInterfaceLanguage()` -- with no provider
 * mounted the hook resolves against `fallbackCatalogue` and renders English
 * successfully, forever.
 *
 * Both throws are ROUTED because the catch site RENDERS the message: the
 * `ApiError` propagates out of `useUploadCustomSticker`'s `mutationFn`,
 * `CardStickerSection`'s `submitUpload` catches it and feeds `cause.message` to
 * a visible `<Notice tone="bad">`. That is the rule `lib/card-dates.ts` states
 * beside `dateTimeLocalToIso`, whose callers catch bare and discard the message.
 */
export async function putStickerBytes(
  t: StickerTranslate,
  presign: Pick<StickerPresign, 'url' | 'headers'>,
  file: Blob,
): Promise<void> {
  let response: Response;

  try {
    response = await fetch(presign.url, {
      method: 'PUT',
      headers: presign.headers,
      body: file,
    });
  } catch (cause) {
    throw new ApiError('internal', t('cardBack.stickers.upload.bytesFailed'), {
      network: [cause instanceof Error ? cause.message : String(cause)],
    });
  }

  if (!response.ok) {
    throw new ApiError(
      'internal',
      t('cardBack.stickers.upload.refused', { status: response.status }),
    );
  }
}

export function confirmCustomSticker(
  boardId: string,
  body: StickerConfirmBody,
): Promise<StickerLibraryRow> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/stickers`,
    stickerLibraryRowSchema,
    stickerConfirmBodySchema.parse(body),
  );
}

export function placeSticker(
  cardId: string,
  body: StickerPlaceBody,
): Promise<PlacedSticker> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/stickers`,
    placedStickerSchema,
    stickerPlaceBodySchema.parse(body),
  );
}

export function moveSticker(
  placementId: string,
  body: StickerUpdateBody,
): Promise<PlacedSticker> {
  return patch(
    `/card-stickers/${encodeURIComponent(placementId)}`,
    placedStickerSchema,
    stickerUpdateBodySchema.parse(body),
  );
}

export function removeSticker(placementId: string): Promise<null> {
  return request(
    `/card-stickers/${encodeURIComponent(placementId)}`,
    emptyResponseSchema,
    {
      method: 'DELETE',
      body: JSON.stringify(stickerRemoveBodySchema.parse({})),
    },
  );
}

export function boardStickersQueryOptions(
  boardId: string,
): UseQueryOptions<StickerLibraryRow[], ApiError> {
  return {
    queryKey: queryKeys.boards.stickers(boardId),
    queryFn: () => getBoardStickers(boardId),
  };
}

export function useBoardStickers(
  boardId: string,
  enabled: boolean,
): UseQueryResult<StickerLibraryRow[], ApiError> {
  return useQuery({ ...boardStickersQueryOptions(boardId), enabled });
}

export function useUploadCustomSticker(
  t: StickerTranslate,
  boardId: string,
): UseMutationResult<StickerLibraryRow, ApiError, UploadCustomStickerVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables) => {
      const contentType = variables.file.type;
      const trimmedName = variables.name?.trim();
      if (variables.file.size > STICKER_UPLOAD_MAX_BYTES) {
        throw new ApiError('validation_failed', t('cardBack.stickers.upload.tooLarge'), {
          sizeBytes: ['exceeds_maximum'],
        });
      }
      if (!isStickerUploadContentType(contentType)) {
        throw new ApiError(
          'validation_failed',
          t('cardBack.stickers.upload.unsupportedType'),
          { contentType: ['unsupported'] },
        );
      }
      const presign = await presignCustomSticker(boardId, {
        fileName: variables.file.name,
        sizeBytes: variables.file.size,
        contentType,
      });
      await putStickerBytes(t, presign, variables.file);
      return confirmCustomSticker(boardId, {
        storageKey: presign.storageKey,
        name: trimmedName === '' ? null : (trimmedName ?? null),
      });
    },
    onSuccess: (sticker) => {
      queryClient.setQueryData<StickerLibraryRow[]>(
        queryKeys.boards.stickers(boardId),
        (previous) => {
          const rows = previous ?? [];
          if (rows.some((row) => row.id === sticker.id)) return rows;
          return [sticker, ...rows];
        },
      );
    },
    onError: () => announce(t('cardBack.stickers.announce.uploadFailed')),
  });
}

export function usePlaceSticker(
  t: StickerTranslate,
  boardId: string,
  cardId: string,
): UseMutationResult<PlacedSticker, ApiError, PlaceStickerVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      placeSticker(cardId, {
        stickerId: variables.stickerId,
        surface: variables.surface,
        positionX: variables.positionX,
        positionY: variables.positionY,
        rotation: variables.rotation ?? 0,
        zIndex: variables.zIndex ?? 0,
      }),
    onSuccess: (sticker) => {
      queryClient.setQueryData<CardDetail>(queryKeys.cards.detail(cardId), (previous) =>
        previous
          ? {
              ...previous,
              stickers: [
                ...previous.stickers.filter((row) => row.id !== sticker.id),
                sticker,
              ],
            }
          : previous,
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
    },
    onError: () => announce(t('cardBack.stickers.announce.placeFailed')),
  });
}

export function useMoveSticker(
  t: StickerTranslate,
  boardId: string,
  cardId: string,
): UseMutationResult<PlacedSticker, ApiError, MoveStickerVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => moveSticker(variables.placementId, variables.body),
    onSuccess: (sticker) => {
      queryClient.setQueryData<CardDetail>(queryKeys.cards.detail(cardId), (previous) =>
        previous
          ? {
              ...previous,
              stickers: previous.stickers.map((row) =>
                row.id === sticker.id ? sticker : row,
              ),
            }
          : previous,
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
    },
    onError: () => announce(t('cardBack.stickers.announce.moveFailed')),
  });
}

export function useRemoveSticker(
  t: StickerTranslate,
  boardId: string,
  cardId: string,
): UseMutationResult<null, ApiError, RemoveStickerVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => removeSticker(variables.placementId),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<CardDetail>(queryKeys.cards.detail(cardId), (previous) =>
        previous
          ? {
              ...previous,
              stickers: previous.stickers.filter(
                (row) => row.id !== variables.placementId,
              ),
            }
          : previous,
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
    },
    onError: () => announce(t('cardBack.stickers.announce.removeFailed')),
  });
}

/**
 * `t` is required and first for the same reason `putStickerBytes` takes one: a
 * plain function cannot call a hook, and an optional translator is exactly what
 * stops `tsc` enumerating the seven component call sites.
 *
 * A named sticker passes through UNROUTED -- that is user data. Only the two
 * fallbacks are copy, and they are TWO keys rather than one `Custom {noun}`
 * message: English builds this by prefixing an adjective, which a German
 * compound or a Spanish postposed adjective cannot follow.
 */
export function stickerLabel(
  t: StickerTranslate,
  sticker: Pick<StickerLibraryRow, 'name' | 'isCustom'>,
): string {
  if (sticker.name !== null && sticker.name.trim() !== '') return sticker.name;
  return sticker.isCustom
    ? t('cardBack.stickers.customNameFallback')
    : t('cardBack.stickers.nameFallback');
}

export function stickerImageSrc(renderUrl: string): string {
  return renderUrl.startsWith('/') ? `/api${renderUrl}` : renderUrl;
}
